/**
 * Documentos anexos de um dono (pessoa, empresa, condomínio, imóvel, veículo, processo). Adicionar
 * segue o padrão do cadastro: um **drawer lateral** (o mesmo de contatos/endereços), não um formulário
 * disputando espaço com a lista. Upload multipart (arquivo + tipo + nome); download autenticado e
 * auditado. Excluir remove só a linha (o blob é endereçado por hash e pode ser compartilhado).
 *
 * Para PESSOA e EMPRESA, o tipo escolhido habilita a **extração de dados por IA**: o documento é
 * lido (RG/CNH… na pessoa; Cartão CNPJ/contrato social… na empresa), os campos propostos aparecem
 * num painel de revisão, e só o que o operador confirmar é gravado.
 */
import { Download, FileText, Paperclip, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { ModalRevisaoExtracao } from '@/components/crud/ModalRevisaoExtracao';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { estadoDaLista } from '@/components/ui/estado';
import { Spinner } from '@/components/ui/misc';
import {
  api,
  baixarArquivo,
  type DocumentoCad,
  enviarArquivoCampos,
  type ExtracaoCad,
  extrairDocumento,
  listarTiposDocumento,
  type TipoDocumentoCad,
} from '@/lib/api';

function tamanhoHumano(bytes: number | null): string {
  if (!bytes) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export function PainelDocumentos({
  ownerTipo,
  ownerId,
  recarregarEntidade,
}: {
  ownerTipo: string;
  ownerId: string;
  /** Recarrega a entidade dona (a Pessoa) — chamado após aplicar a extração, para o formulário
   * de dados abaixo refletir os campos recém-gravados (CPF, RG, filiação, expedição…). */
  recarregarEntidade?: () => Promise<void>;
}) {
  const base = `/cadastro/${ownerTipo}/${ownerId}/documentos`;
  // Extração por IA: pessoa (RG/CNH…), empresa (Cartão CNPJ…) e veículo (CRLV → dados + dono).
  const podeExtrair =
    ownerTipo === 'pessoa' || ownerTipo === 'empresa' || ownerTipo === 'veiculo';
  const [docs, setDocs] = useState<DocumentoCad[]>([]);
  const [tipos, setTipos] = useState<TipoDocumentoCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  // Campos do drawer de adição.
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [tipoSel, setTipoSel] = useState('');
  const [categoriaLivre, setCategoriaLivre] = useState('');
  const [extrairAoEnviar, setExtrairAoEnviar] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [extraindo, setExtraindo] = useState<string | null>(null);
  const [revisao, setRevisao] = useState<ExtracaoCad | null>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const extraivel = (id: string | null) => !!id && tipos.some((t) => t.id === id);

  async function carregar() {
    const r = await api.get<DocumentoCad[]>(base);
    setCarregando(false);
    if (r.ok) setDocs(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    void listarTiposDocumento(ownerTipo).then((r) => r.ok && setTipos(r.dados));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  async function extrair(d: DocumentoCad) {
    setExtraindo(d.id);
    const aviso = toast.loading('Lendo o documento e extraindo os dados…');
    const r = await extrairDocumento(d.id);
    setExtraindo(null);
    toast.dismiss(aviso);
    if (!r.ok) return void toast.error(r.erro);
    if (Object.keys(r.dados.campos).length === 0) {
      return void toast('Nenhum dado legível foi encontrado neste documento.');
    }
    setRevisao(r.dados);
  }

  function limpar() {
    setArquivo(null);
    setTipoSel('');
    setCategoriaLivre('');
  }

  async function salvar(): Promise<boolean> {
    if (!arquivo) return false;
    const categoria = tipoSel === 'outro' ? categoriaLivre.trim() : tipoSel;
    const campos: Record<string, string> = { nome: arquivo.name };
    if (categoria) campos.categoria = categoria;
    setSalvando(true);
    const r = await enviarArquivoCampos<DocumentoCad>(base, arquivo, campos);
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Documento anexado.');
    await carregar();
    // Extração opcional logo após anexar (só pessoa + tipo suportado) — abre a revisão.
    if (podeExtrair && extrairAoEnviar && extraivel(categoria)) await extrair(r.dados);
    return true;
  }

  async function baixar(d: DocumentoCad) {
    const ok = await baixarArquivo(`/cadastro/documentos/${d.id}/download`, d.nome, 'GET');
    if (!ok) toast.error('Não foi possível baixar o documento.');
  }

  async function excluir(d: DocumentoCad) {
    const r = await api.del(`/cadastro/documentos/${d.id}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Documento removido do dono.');
    void carregar();
  }

  const rotuloTipo = (id: string | null) => tipos.find((t) => t.id === id)?.rotulo ?? id ?? '';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="size-4 text-accent" />
          Documentos
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, docs.length === 0, 'Nenhum documento anexado.') ?? (
          <div className="flex flex-col gap-2">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[15px] text-text">{d.nome}</span>
                  <span className="tipo-corpo-pequeno text-text-tertiary">
                    {[rotuloTipo(d.categoria), tamanhoHumano(d.tamanho)].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {podeExtrair && extraivel(d.categoria) && (
                    <Button
                      variant="fantasma"
                      size="sm"
                      title="Extrair dados deste documento"
                      aria-label="Extrair dados"
                      disabled={extraindo === d.id}
                      onClick={() => void extrair(d)}
                    >
                      {extraindo === d.id ? <Spinner className="size-4" /> : <Sparkles />}
                    </Button>
                  )}
                  <Button variant="fantasma" size="sm" onClick={() => void baixar(d)}>
                    <Download />
                  </Button>
                  <Button
                    variant="fantasma"
                    size="sm"
                    className="text-danger hover:text-danger"
                    onClick={() => void excluir(d)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo documento"
          descricao={
            podeExtrair
              ? 'Escolha o tipo e o arquivo. Se o tipo for suportado, os dados podem ser extraídos e revisados antes de gravar.'
              : 'Escolha o arquivo e, se quiser, um tipo.'
          }
          rotuloAbrir="Adicionar documento"
          rotuloSalvar="Anexar"
          podeSalvar={!!arquivo}
          salvando={salvando}
          onSalvar={salvar}
          onFechar={limpar}
        >
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Tipo do documento</span>
            <select
              value={tipoSel}
              onChange={(e) => setTipoSel(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            >
              <option value="">— selecione —</option>
              {tipos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.rotulo}
                </option>
              ))}
              <option value="outro">Outro (sem extração)</option>
            </select>
          </label>

          {tipoSel === 'outro' && (
            <label className="flex flex-col gap-2">
              <span className="tipo-rotulo">Categoria</span>
              <Input
                value={categoriaLivre}
                onChange={(e) => setCategoriaLivre(e.target.value)}
                placeholder="ex.: contrato, procuração"
              />
            </label>
          )}

          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Arquivo</span>
            <input
              ref={arquivoRef}
              type="file"
              className="hidden"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
            <div className="flex items-center gap-3">
              <Button type="button" variant="secundario" onClick={() => arquivoRef.current?.click()}>
                <Paperclip />
                {arquivo ? 'Trocar arquivo' : 'Escolher arquivo'}
              </Button>
              <span className="tipo-corpo-pequeno truncate text-text-tertiary">
                {arquivo?.name ?? 'nenhum arquivo escolhido'}
              </span>
            </div>
          </div>

          {podeExtrair && extraivel(tipoSel) && (
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={extrairAoEnviar}
                onChange={(e) => setExtrairAoEnviar(e.target.checked)}
                className="size-4 accent-[var(--primary)]"
              />
              <span className="tipo-corpo-pequeno text-text-secondary">
                Extrair dados do documento ao anexar (revisão antes de gravar)
              </span>
            </label>
          )}
        </DrawerAdicionar>
      </CardContent>

      {podeExtrair && (
        <ModalRevisaoExtracao
          extracao={revisao}
          ownerTipo={ownerTipo}
          ownerId={ownerId}
          onFechar={() => setRevisao(null)}
          onAplicado={async () => {
            await carregar();
            await recarregarEntidade?.(); // atualiza o formulário de dados abaixo
          }}
        />
      )}
    </Card>
  );
}
